import { FormControl, Grid, MenuItem, SelectChangeEvent } from '@mui/material';
import { StyledSelect, StyledFormLabel } from 'components/atoms';
import {
  OptionFieldProps,
  withGridItemCSS,
  withWrapperCSS,
} from 'components/organisms/forms/form-builder/types';
import { noop } from 'lodash';
import React, { FC, useEffect, useRef, useState } from 'react';

import { MenuProps } from './';

export const SelectField: FC<
  OptionFieldProps<string> & withGridItemCSS & withWrapperCSS
> = ({
  defaultValue = '',
  disabled = false,
  disabledOptions = [],
  error,
  fieldKey,
  gridItemCss,
  initValue = '',
  label = '',
  onChange = noop,
  onBlur = noop,
  hide = false,
  options,
  labelCss,
  required = false,
  wrapperCss,
  xs = 12,
}) => {
  const [value, setValue] = useState(initValue != '' ? initValue : defaultValue);
  const [hasError, setHasError] = useState(error ? error : false);
  const previousDefaultValue = useRef<string>();
  const previousInitValue = useRef(initValue);
  const userSelectedValue = useRef(false);

  useEffect(() => {
    if (hide) {
      onChange('', fieldKey);
      setValue(initValue);
    }
  }, [hide]);

  useEffect(() => {
    if (previousInitValue.current !== initValue) {
      previousInitValue.current = initValue;
      userSelectedValue.current = false;

      const nextValue = initValue !== '' ? initValue : defaultValue;
      setValue(nextValue);
      onChange(nextValue, fieldKey);
    }
  }, [defaultValue, fieldKey, initValue, onChange]);

  useEffect(() => {
    const defaultValueChanged = previousDefaultValue.current !== defaultValue;
    previousDefaultValue.current = defaultValue;

    if (!hide && initValue === '' && defaultValueChanged && !userSelectedValue.current) {
      setValue(defaultValue);
      if (defaultValue !== '') onChange(defaultValue, fieldKey);
    }
  }, [defaultValue, fieldKey, hide, initValue, onChange]);

  useEffect(() => {
    setHasError(error ? error : false);
  }, [error]);

  const selectChange = (event: SelectChangeEvent<string>) => {
    const newValue = event.target.value as string;
    if (required && newValue === '') setHasError(true);
    else setHasError(false);
    userSelectedValue.current = true;
    setValue(newValue);
    onChange(newValue, fieldKey);
  };

  return hide ? null : (
    <Grid item xs={xs} style={gridItemCss}>
      <FormControl
        error={!!hasError}
        required={required}
        style={{ width: '100%', ...wrapperCss }}
      >
        <StyledFormLabel error={!!hasError} style={labelCss}>
          {label} {required ? '*' : null}
        </StyledFormLabel>
        <StyledSelect
          data-testid={`select-${fieldKey}`}
          disabled={disabled}
          value={value}
          required={required}
          onChange={(event: any) => selectChange(event)}
          MenuProps={MenuProps}
          onBlur={onBlur}
        >
          {options.map((option) => (
            <MenuItem
              key={`${fieldKey}-option-${option.name}`}
              value={option.name}
              disabled={disabledOptions.includes(option.name.toString())}
            >
              {option.description}
            </MenuItem>
          ))}
        </StyledSelect>
      </FormControl>
    </Grid>
  );
};
